/**
 * White-Label-Brand fuer die SPA (Client-Kopie von `shared/branding`).
 *
 * Die Clients bauen als eigenstaendige Vite-Apps ohne Workspace-Zugriff
 * (Docker-Build-Kontext = App-Unterverzeichnis, siehe vite.config.ts), daher
 * liegt die Ramp-Ableitung hier als identische Kopie von
 * `shared/branding/src/index.ts#deriveColorRamp`. Aenderungen an der
 * Mischformel muessen in beiden Dateien landen, sonst weichen die Farben
 * zwischen Server-Mails/Keycloak und den SPAs ab.
 *
 * Reines Modul: keine React-Abhaengigkeit, damit die Helfer unter vitest
 * (jsdom) direkt testbar sind. `fetchPublicBrand` wirft nie — jeder Fehler
 * (Netz, 5xx, kaputtes JSON) faellt auf die Haus-Defaults zurueck, damit ein
 * Haus-Deployment exakt wie bisher rendert.
 */

export interface BrandConfig {
  slug: string;
  /** Kurzer Firmen-/Markenname, z. B. "SecTepe". */
  name: string;
  /** Produktname fuer Titel, z. B. "SecTepe.Comm". */
  productName: string;
  /** App-Switcher-Label (≤ 12 Zeichen). */
  shortName: string;
  tagline: string;
  /** Mittlere Markenfarbe (`#rrggbb`), Ramp-Stufe 500. */
  primaryColor: string;
  /** Optionale Akzentfarbe; leer = Ramp-Stufe 300. */
  accentColor: string;
  logoUrl: string;
  logoUrlDark: string;
  faviconUrl: string;
  supportEmail: string;
  supportUrl: string;
  docsUrl: string;
  imprintUrl: string;
  privacyUrl: string;
  termsUrl: string;
  websiteUrl: string;
  emailFromName: string;
  emailFooter: string;
}

export type BrandSource = 'tenant-brand' | 'default-brand' | 'env' | 'house';

export interface PublicBrandResponse {
  brand: BrandConfig;
  source: BrandSource;
  /** Basis-Domain der Plattform (TENANT_DNS_ZONE / BASE_DOMAIN / DOMAIN des Deployments). */
  domain: string;
}

/** Haus-Domain; gilt nur, solange der Server keine `domain` liefert. */
export const HOUSE_DOMAIN = 'sectepe.com';

const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

/** Server-`domain` defensiv uebernehmen: nur ein plausibler FQDN, sonst Haus-Domain. */
export function normalizeDomain(input: unknown): string {
  if (typeof input !== 'string') return HOUSE_DOMAIN;
  const v = input.trim().toLowerCase();
  return DOMAIN_RE.test(v) ? v : HOUSE_DOMAIN;
}

export const HOUSE_BRAND_SLUG = 'sectepe';

/** Haus-Defaults — identisch zu `SECTEPE_DEFAULTS` in shared/branding. */
export const HOUSE_BRAND: Readonly<BrandConfig> = Object.freeze({
  slug: HOUSE_BRAND_SLUG,
  name: 'SecTepe',
  productName: 'SecTepe.Comm',
  shortName: 'SecTepe',
  tagline: '',
  primaryColor: '#d8524c',
  accentColor: '',
  logoUrl: '',
  logoUrlDark: '',
  faviconUrl: '',
  supportEmail: '',
  supportUrl: '',
  docsUrl: '',
  imprintUrl: '',
  privacyUrl: '',
  termsUrl: '',
  websiteUrl: '',
  emailFromName: '',
  emailFooter: '',
});

export const BRAND_KEYS = Object.keys(HOUSE_BRAND) as Array<keyof BrandConfig>;

const COLOR_KEYS: ReadonlySet<keyof BrandConfig> = new Set(['primaryColor', 'accentColor']);
const URL_KEYS: ReadonlySet<keyof BrandConfig> = new Set([
  'logoUrl', 'logoUrlDark', 'faviconUrl', 'supportUrl', 'docsUrl',
  'imprintUrl', 'privacyUrl', 'termsUrl', 'websiteUrl',
]);

/** Pfad des oeffentlichen Brand-Endpunkts — gleiche Basis wie der axios-Client (`/api`). */
export const PUBLIC_BRAND_PATH = '/api/public/brand';

export const RAMP_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
export type RampStep = (typeof RAMP_STEPS)[number];
export type ColorRamp = Record<RampStep, string>;

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_RE.test(value);
}

/** Nur absolute https-URLs sind als Logo-/Link-Ziel zulaessig (kein javascript:/data:). */
export function isHttpsUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value === '') return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Sicherer Favicon-href: relativ gegen die Dokumentbasis aufgeloest, nur
 * http(s). Haelt javascript:/data: aus dem `href` heraus, auch beim
 * Zuruecksetzen des urspruenglichen (seitenrelativen) Favicons
 * (CodeQL js/xss-through-dom).
 */
export function isSafeIconHref(value: string, doc: Document = document): boolean {
  if (!value) return false;
  try {
    const u = new URL(value, doc.baseURI);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function isEmailAddress(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 254 && EMAIL_RE.test(value);
}

/**
 * Server-Antwort defensiv auf die Haus-Defaults mergen: nur Strings werden
 * uebernommen, leere Werte bedeuten "erben", Farben muessen `#rrggbb` sein,
 * URLs https. So kann eine kaputte Zeile nie zu einem XSS-Sink im `src`/
 * `href` oder zu einer ungueltigen CSS-Variable werden.
 */
export function normalizeBrand(input: unknown): BrandConfig {
  const out: BrandConfig = { ...HOUSE_BRAND };
  if (!input || typeof input !== 'object') return out;
  const raw = input as Record<string, unknown>;
  for (const key of BRAND_KEYS) {
    const v = raw[key];
    if (typeof v !== 'string') continue;
    const t = v.trim();
    if (t === '') continue;
    if (COLOR_KEYS.has(key)) {
      if (isHexColor(t)) out[key] = t.toLowerCase();
    } else if (URL_KEYS.has(key)) {
      if (isHttpsUrl(t)) out[key] = t;
    } else {
      out[key] = t;
    }
  }
  return out;
}

/** True, wenn die Markenfarbe der Haus-Farbe entspricht (Tokens bleiben unangetastet). */
export function hasHouseColor(brand: Pick<BrandConfig, 'primaryColor' | 'accentColor'>): boolean {
  return brand.primaryColor.toLowerCase() === HOUSE_BRAND.primaryColor && brand.accentColor === '';
}

/** Effektiver Mail-Absendername (explizit oder `${name} Benachrichtigungen`). */
export function emailFromName(brand: Pick<BrandConfig, 'name' | 'emailFromName'>): string {
  return brand.emailFromName || `${brand.name} Benachrichtigungen`;
}

/** Logo fuer dunkle Hintergruende (faellt auf das helle zurueck). */
export function logoUrlDark(brand: Pick<BrandConfig, 'logoUrl' | 'logoUrlDark'>): string {
  return brand.logoUrlDark || brand.logoUrl;
}

// ---------------------------------------------------------------------------
// Farb-Ramp (1:1 aus shared/branding)
// ---------------------------------------------------------------------------

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function mix(base: Rgb, target: Rgb, amount: number): string {
  return rgbToHex(
    base[0] + (target[0] - base[0]) * amount,
    base[1] + (target[1] - base[1]) * amount,
    base[2] + (target[2] - base[2]) * amount,
  );
}

/**
 * 50–900-Ramp mit der Farbe auf Stufe 500: hellere Stufen mischen Richtung
 * Weiss, dunklere Richtung Schwarz. Deterministisch — alle Oberflaechen
 * (Server-Mails, Keycloak, SPAs) berechnen dieselben Werte.
 */
export function deriveColorRamp(hex: string): ColorRamp {
  const base = isHexColor(hex) ? hexToRgb(hex) : hexToRgb(HOUSE_BRAND.primaryColor);
  const white: Rgb = [255, 255, 255];
  const black: Rgb = [0, 0, 0];
  return {
    50: mix(base, white, 0.92),
    100: mix(base, white, 0.82),
    200: mix(base, white, 0.64),
    300: mix(base, white, 0.42),
    400: mix(base, white, 0.2),
    500: rgbToHex(base[0], base[1], base[2]),
    600: mix(base, black, 0.15),
    700: mix(base, black, 0.3),
    800: mix(base, black, 0.45),
    900: mix(base, black, 0.6),
  };
}

/** Relative Luminanz (WCAG) fuer die Wahl lesbarer Schrift auf der Markenfarbe. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(isHexColor(hex) ? hex : HOUSE_BRAND.primaryColor).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** `#ffffff` oder `#000000`, je nachdem was besser auf `hex` kontrastiert. */
export function contrastText(hex: string): string {
  return relativeLuminance(hex) > 0.4 ? '#000000' : '#ffffff';
}

function rgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ---------------------------------------------------------------------------
// DOM-Anwendung (Titel, Favicon, --sec-* Tokens)
// ---------------------------------------------------------------------------

/**
 * Design-System-Tokens, die eine Fremdmarke ueberschreibt. Sie werden als
 * Inline-Style auf <html> gesetzt und schlagen damit die `:root`-Defaults aus
 * dem gespiegelten tokens.css. Bei Haus-Farbe werden sie entfernt, damit das
 * Haus-Deployment pixelgleich zu vorher bleibt.
 */
export const BRAND_TOKEN_NAMES: readonly string[] = Object.freeze([
  ...RAMP_STEPS.map((s) => `--sec-coral-${s}`),
  '--sec-coral',
  '--sec-accent',
  '--sec-accent-hover',
  '--sec-accent-press',
  '--sec-accent-soft',
  '--sec-border-coral',
  '--sec-fg-on-coral',
  '--sec-danger',
  '--sec-shadow-coral',
  '--sec-shadow-focus',
]);

/** Token → Wert fuer eine Marke; leer bei Haus-Farbe. */
export function brandTokenValues(brand: Pick<BrandConfig, 'primaryColor' | 'accentColor'>): Record<string, string> {
  if (hasHouseColor(brand)) return {};
  const ramp = deriveColorRamp(brand.primaryColor);
  const accent = isHexColor(brand.accentColor) ? brand.accentColor.toLowerCase() : ramp[300];
  const out: Record<string, string> = {};
  for (const step of RAMP_STEPS) out[`--sec-coral-${step}`] = ramp[step];
  out['--sec-coral'] = accent;
  out['--sec-accent'] = accent;
  out['--sec-accent-hover'] = ramp[400];
  out['--sec-accent-press'] = ramp[500];
  out['--sec-accent-soft'] = ramp[100];
  out['--sec-border-coral'] = accent;
  out['--sec-fg-on-coral'] = contrastText(accent);
  // Fehler-/Gefahr-Semantik bleibt rot, auch wenn die Marke z. B. blau ist.
  out['--sec-danger'] = HOUSE_BRAND.primaryColor;
  out['--sec-shadow-coral'] = `0 12px 32px ${rgba(accent, 0.35)}`;
  out['--sec-shadow-focus'] = `0 0 0 3px ${rgba(accent, 0.55)}`;
  return out;
}

// Urspruenglichen Favicon-href je <link> im Modulspeicher halten, nicht in
// einem data-Attribut: so wird der Wert beim Zuruecksetzen nicht aus dem DOM
// zurueckgelesen (getAttribute -> setAttribute), was CodeQL als DOM-XSS wertet.
const originalFaviconHref = new WeakMap<HTMLLinkElement, string>();

/**
 * Wendet Farb-Tokens und Favicon einer Marke auf das Dokument an. Idempotent
 * und reversibel: Haus-Werte entfernen alle Overrides wieder (relevant, wenn
 * der Tenant-Slug nach dem Login von "default" auf eine Tenant-Marke wechselt).
 */
export function applyBrandToDocument(brand: BrandConfig, doc: Document = document): void {
  const root = doc.documentElement;
  const values = brandTokenValues(brand);
  for (const name of BRAND_TOKEN_NAMES) {
    const v = values[name];
    if (v) root.style.setProperty(name, v);
    else root.style.removeProperty(name);
  }

  const icon = doc.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (icon) {
    if (isHttpsUrl(brand.faviconUrl)) {
      if (!originalFaviconHref.has(icon)) {
        originalFaviconHref.set(icon, icon.getAttribute('href') ?? '');
      }
      icon.setAttribute('href', brand.faviconUrl);
      icon.removeAttribute('type');
    } else if (originalFaviconHref.has(icon)) {
      const original = originalFaviconHref.get(icon) ?? '';
      if (isSafeIconHref(original, doc)) icon.setAttribute('href', original);
      else icon.removeAttribute('href');
      originalFaviconHref.delete(icon);
    }
  }
}

// ---------------------------------------------------------------------------
// Laden
// ---------------------------------------------------------------------------

export interface FetchPublicBrandOptions {
  /** Tenant-Slug → `?tenant=<slug>` (Marke des Tenants statt Deployment-Default). */
  tenant?: string | null;
  signal?: AbortSignal;
  /** Test-Hook; Default `globalThis.fetch`. */
  fetchImpl?: typeof fetch;
}

/**
 * `GET /api/public/brand[?tenant=]` — oeffentlich, ohne Auth-Header (der
 * Endpunkt ist `Cache-Control: public`, ein Bearer wuerde nur Caches
 * fragmentieren). Wirft nie: jeder Fehler ergibt die Haus-Defaults mit
 * `source: 'house'`.
 */
export async function fetchPublicBrand(opts: FetchPublicBrandOptions = {}): Promise<PublicBrandResponse> {
  const fetchImpl = opts.fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined);
  if (!fetchImpl) return { brand: { ...HOUSE_BRAND }, source: 'house', domain: HOUSE_DOMAIN };
  const tenant = opts.tenant?.trim();
  const url = tenant ? `${PUBLIC_BRAND_PATH}?tenant=${encodeURIComponent(tenant)}` : PUBLIC_BRAND_PATH;
  try {
    const res = await fetchImpl(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: opts.signal,
    });
    if (!res.ok) return { brand: { ...HOUSE_BRAND }, source: 'house', domain: HOUSE_DOMAIN };
    const body = (await res.json()) as Partial<PublicBrandResponse> | null;
    const source: BrandSource =
      body?.source === 'tenant-brand' || body?.source === 'default-brand' || body?.source === 'env'
        ? body.source
        : 'house';
    return { brand: normalizeBrand(body?.brand), source, domain: normalizeDomain(body?.domain) };
  } catch {
    return { brand: { ...HOUSE_BRAND }, source: 'house', domain: HOUSE_DOMAIN };
  }
}
